import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Ticket, Plus, Clock, CheckCircle2, AlertTriangle, ArrowRight,
  MessageSquare, Send, Building2, Filter, User, XCircle, Loader2, Sparkles
} from 'lucide-react';
import useJevSuggest from '../hooks/useJevSuggest';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'operations', label: 'Operations', color: 'bg-orange-100 text-orange-700' },
  { value: 'finance', label: 'Finance', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'maintenance', label: 'Maintenance', color: 'bg-blue-100 text-blue-700' },
  { value: 'training', label: 'Training', color: 'bg-purple-100 text-purple-700' },
  { value: 'hr', label: 'HR / People', color: 'bg-pink-100 text-pink-700' },
  { value: 'executive', label: 'Executive / Escalation', color: 'bg-red-100 text-red-700' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'bg-slate-100 text-slate-600' },
  { value: 'medium', label: 'Medium', color: 'bg-amber-100 text-amber-700' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700' },
];

const STATUSES = [
  { value: 'open', label: 'Open', color: 'bg-blue-100 text-blue-700', icon: Clock },
  { value: 'in_progress', label: 'In Progress', color: 'bg-amber-100 text-amber-700', icon: ArrowRight },
  { value: 'resolved', label: 'Resolved', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  { value: 'closed', label: 'Closed', color: 'bg-slate-100 text-slate-600', icon: XCircle },
];

function CategoryBadge({ category }) {
  const cat = CATEGORIES.find(c => c.value === category);
  return <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${cat?.color || 'bg-slate-100 text-slate-600'}`}>{cat?.label || category}</span>;
}

function PriorityBadge({ priority }) {
  const p = PRIORITIES.find(pr => pr.value === priority);
  return <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${p?.color || 'bg-slate-100 text-slate-600'}`}>{p?.label || priority}</span>;
}

function StatusBadge({ status }) {
  const s = STATUSES.find(st => st.value === status);
  const Icon = s?.icon || Clock;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${s?.color || 'bg-slate-100 text-slate-600'}`}>
      <Icon className="w-3 h-3" />{s?.label || status}
    </span>
  );
}

function TicketDetailDialog({ ticket, open, onClose, onUpdate, user }) {
  const [comment, setComment] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [resolution, setResolution] = useState('');
  const [sending, setSending] = useState(false);

  if (!ticket) return null;

  const handleComment = async () => {
    if (!comment.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/service-tickets/${ticket.id}/comments`, { content: comment });
      setComment('');
      onUpdate();
      toast.success('Comment added');
    } catch { toast.error('Failed to add comment'); }
    finally { setSending(false); }
  };

  const handleStatusChange = async (status) => {
    try {
      const body = { status };
      if (status === 'resolved' && resolution) body.resolution_notes = resolution;
      await axios.put(`${API_URL}/service-tickets/${ticket.id}`, body);
      onUpdate();
      toast.success(`Ticket marked as ${status.replace('_', ' ')}`);
    } catch { toast.error('Failed to update ticket'); }
  };

  const canManage = ['owner', 'area_manager'].includes(user?.role) || ticket.assigned_to_email === user?.email;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-900 pr-8" data-testid="ticket-detail-title">{ticket.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          {/* Meta */}
          <div className="flex flex-wrap gap-2">
            <StatusBadge status={ticket.status} />
            <CategoryBadge category={ticket.category} />
            <PriorityBadge priority={ticket.priority} />
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-4 text-sm bg-slate-50 p-4 rounded-lg">
            <div><span className="text-slate-500">Created by:</span> <span className="font-medium">{ticket.created_by_name}</span></div>
            <div><span className="text-slate-500">Store:</span> <span className="font-medium">{ticket.store_name}</span></div>
            <div><span className="text-slate-500">Assigned to:</span> <span className="font-medium">{ticket.assigned_to_name}</span></div>
            <div><span className="text-slate-500">Created:</span> <span className="font-medium">{new Date(ticket.created_at).toLocaleDateString()}</span></div>
          </div>

          {/* Description */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Description</h3>
            <p className="text-slate-600 bg-white border border-slate-200 rounded-lg p-4 text-sm">{ticket.description}</p>
          </div>

          {/* Resolution */}
          {ticket.resolution_notes && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-green-700 mb-1">Resolution</h3>
              <p className="text-green-600 text-sm">{ticket.resolution_notes}</p>
            </div>
          )}

          {/* Status Actions */}
          {canManage && ticket.status !== 'closed' && (
            <div className="border border-slate-200 rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-semibold text-slate-700">Update Status</h3>
              {ticket.status === 'open' && (
                <Button size="sm" onClick={() => handleStatusChange('in_progress')} className="bg-amber-500 hover:bg-amber-600 text-white">
                  <ArrowRight className="w-3 h-3 mr-1" /> Start Working
                </Button>
              )}
              {['open', 'in_progress'].includes(ticket.status) && (
                <div className="space-y-2">
                  <Input placeholder="Resolution notes (optional)" value={resolution} onChange={(e) => setResolution(e.target.value)} />
                  <Button size="sm" onClick={() => handleStatusChange('resolved')} className="bg-green-500 hover:bg-green-600 text-white">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Mark Resolved
                  </Button>
                </div>
              )}
              {ticket.status === 'resolved' && (
                <Button size="sm" onClick={() => handleStatusChange('closed')} variant="outline">
                  <XCircle className="w-3 h-3 mr-1" /> Close Ticket
                </Button>
              )}
            </div>
          )}

          {/* Comments */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Comments ({ticket.comments?.length || 0})</h3>
            <div className="space-y-3 max-h-60 overflow-y-auto">
              {(ticket.comments || []).map(c => (
                <div key={c.id} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 text-xs font-bold shrink-0">
                    {c.author_name?.charAt(0)}
                  </div>
                  <div className="flex-1 bg-slate-50 rounded-lg p-3">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-slate-900">{c.author_name}</span>
                      <span className="text-xs text-slate-400">{new Date(c.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-sm text-slate-600">{c.content}</p>
                  </div>
                </div>
              ))}
              {(!ticket.comments || ticket.comments.length === 0) && (
                <p className="text-sm text-slate-400 text-center py-4">No comments yet</p>
              )}
            </div>
            <div className="flex gap-2 mt-3">
              <Input
                placeholder="Add a comment..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleComment()}
                data-testid="ticket-comment-input"
              />
              <Button onClick={handleComment} disabled={sending || !comment.trim()} size="icon" className="bg-orange-500 hover:bg-orange-600">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ServiceTickets() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortMode, setSortMode] = useState('urgency');
  const [stores, setStores] = useState([]);
  const [form, setForm] = useState({ title: '', description: '', category: 'operations', priority: 'medium', store_id: '' });
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState({ category: false, priority: false });
  const triageEnabled = createOpen && form.title.trim().length >= 4 && form.description.trim().length >= 12;
  const { suggestion: triage, loading: triaging } = useJevSuggest('ticket', { title: form.title, description: form.description }, triageEnabled);

  // Rooty's fast triage fills category/priority until the user picks their own.
  useEffect(() => {
    if (!triage) return;
    setForm(f => ({
      ...f,
      category: (!touched.category && triage.apply_category) ? triage.category.value : f.category,
      priority: (!touched.priority && triage.apply_priority) ? triage.priority.value : f.priority,
    }));
  }, [triage, touched.category, touched.priority]);

  const fetchStores = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/stores`);
      setStores(res.data);
    } catch (err) {
      console.warn('Failed to fetch stores', err);
    }
  }, []);

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/service-tickets`);
      setTickets(res.data);
    } catch (err) { toast.error('Failed to load tickets'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTickets(); fetchStores(); }, [fetchTickets, fetchStores]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) { toast.error('Please fill in all fields'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/service-tickets`, form);
      toast.success('Ticket created! Assigned to department head.');
      setCreateOpen(false);
      setForm({ title: '', description: '', category: 'operations', priority: 'medium', store_id: '' });
      setTouched({ category: false, priority: false });
      fetchTickets();
    } catch (err) {
      console.warn('Ticket create failed', err);
      toast.error('Failed to create ticket');
    }
    finally { setSubmitting(false); }
  };

  const openDetail = async (ticket) => {
    try {
      const res = await axios.get(`${API_URL}/service-tickets/${ticket.id}`);
      setSelectedTicket(res.data);
      setDetailOpen(true);
    } catch (err) {
      console.warn('Ticket fetch failed', err);
      toast.error('Failed to load ticket');
    }
  };

  const refreshDetail = async () => {
    if (!selectedTicket) return;
    try {
      const res = await axios.get(`${API_URL}/service-tickets/${selectedTicket.id}`);
      setSelectedTicket(res.data);
      fetchTickets();
    } catch (err) {
      console.warn('Ticket refresh failed', err);
    }
  };

  const filtered = tickets.filter(t => {
    if (filterStatus !== 'all' && t.status !== filterStatus) return false;
    if (filterCategory !== 'all' && t.category !== filterCategory) return false;
    return true;
  });
  // Backend already orders by urgency (escalated → open → Rooty score); "Newest" re-sorts client-side.
  const ordered = sortMode === 'newest'
    ? [...filtered].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
    : filtered;
  const pinnedCount = ordered.filter(t => t.escalated && ['open', 'in_progress'].includes(t.status)).length;

  const stats = {
    open: tickets.filter(t => t.status === 'open').length,
    in_progress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
    total: tickets.length,
  };

  return (
    <div className="space-y-6" data-testid="service-tickets-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Service Tickets</h1>
          <p className="text-sm text-slate-500 mt-0.5">Raise and track issues across departments</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl h-9 px-4 text-[13px] font-medium btn-press" data-testid="create-ticket-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Ticket
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border border-slate-200/80 rounded-2xl"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-blue-600 tabular-nums">{stats.open}</p><p className="text-[11px] text-slate-500 mt-0.5">Open</p>
        </CardContent></Card>
        <Card className="border border-slate-200/80 rounded-2xl"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-amber-600 tabular-nums">{stats.in_progress}</p><p className="text-[11px] text-slate-500 mt-0.5">In Progress</p>
        </CardContent></Card>
        <Card className="border border-slate-200/80 rounded-2xl"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-green-600 tabular-nums">{stats.resolved}</p><p className="text-[11px] text-slate-500 mt-0.5">Resolved</p>
        </CardContent></Card>
        <Card className="border border-slate-200/80 rounded-2xl"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-slate-700 tabular-nums">{stats.total}</p><p className="text-[11px] text-slate-500 mt-0.5">Total</p>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40 border-slate-200" data-testid="filter-status">
            <Filter className="w-4 h-4 mr-2 text-slate-400" /><SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-44 border-slate-200" data-testid="filter-category">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 text-xs" data-testid="ticket-sort-toggle">
          {[['urgency', 'By urgency'], ['newest', 'Newest']].map(([v, label]) => (
            <button key={v} type="button" onClick={() => setSortMode(v)} data-testid={`ticket-sort-${v}`}
              className={`px-3 py-1.5 rounded-md font-medium transition-colors ${sortMode === v ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
              {v === 'urgency' && <Sparkles className="inline w-3 h-3 mr-1 -mt-0.5" />}{label}
            </button>
          ))}
        </div>
      </div>

      {/* Ticket List */}
      <div className="space-y-3">
        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" /></div>
        ) : filtered.length === 0 ? (
          <Card className="border-slate-200"><CardContent className="py-12 text-center">
            <Ticket className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500 font-medium">No tickets found</p>
            <p className="text-sm text-slate-400">Create a new ticket to get started</p>
          </CardContent></Card>
        ) : (
          ordered.map((ticket, idx) => {
            const isPinned = sortMode === 'urgency' && ticket.escalated && ['open', 'in_progress'].includes(ticket.status);
            const showPinnedHeader = isPinned && idx === 0;
            const showRestHeader = sortMode === 'urgency' && pinnedCount > 0 && idx === pinnedCount;
            return (
            <div key={ticket.id}>
            {showPinnedHeader && (
              <p className="text-[11px] font-semibold uppercase tracking-wide text-red-600 flex items-center gap-1 mb-2" data-testid="pinned-header">
                <AlertTriangle className="w-3 h-3" /> Pinned · owners alerted ({pinnedCount})
              </p>
            )}
            {showRestHeader && (
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 flex items-center gap-1 mb-2 mt-4" data-testid="urgency-header">
                <Sparkles className="w-3 h-3" /> Sorted by Rooty's urgency
              </p>
            )}
            <Card
              className={`transition-all cursor-pointer hover:shadow-sm ${isPinned ? 'border-red-300 bg-red-50/40 hover:border-red-400' : 'border-slate-200 hover:border-orange-300'}`}
              onClick={() => openDetail(ticket)}
              data-testid={`ticket-${ticket.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 truncate">{ticket.title}</h3>
                    <p className="text-sm text-slate-500 mt-1 line-clamp-1">{ticket.description}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      <StatusBadge status={ticket.status} />
                      <CategoryBadge category={ticket.category} />
                      <PriorityBadge priority={ticket.priority} />
                      {ticket.escalated && <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-red-600 text-white" data-testid={`ticket-escalated-${ticket.id}`}>Owner alerted</span>}
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Building2 className="w-3 h-3" />{ticket.store_name}
                      </span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="flex items-center gap-1 text-xs text-slate-400">
                      <User className="w-3 h-3" />{ticket.assigned_to_name}
                    </div>
                    <p className="text-xs text-slate-400 mt-1">{new Date(ticket.created_at).toLocaleDateString()}</p>
                    {ticket.comments?.length > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-400 mt-1">
                        <MessageSquare className="w-3 h-3" />{ticket.comments.length}
                      </span>
                    )}
                    {typeof ticket.urgency_score === 'number' && (
                      <p className="text-[10px] text-slate-400 mt-1" title="Rooty urgency score (0–4)" data-testid={`ticket-urgency-${ticket.id}`}>
                        Urgency {ticket.urgency_score.toFixed(1)}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            </div>
            );
          })
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">Create Service Ticket</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input placeholder="Brief summary of the issue" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required data-testid="ticket-title-input" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <textarea
                className="w-full min-h-[100px] rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Describe the issue in detail..."
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                required
                data-testid="ticket-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={v => { setTouched(t => ({ ...t, category: true })); setForm({ ...form, category: v }); }}>
                  <SelectTrigger data-testid="ticket-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={v => { setTouched(t => ({ ...t, priority: true })); setForm({ ...form, priority: v }); }}>
                  <SelectTrigger data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {stores.length > 0 && (
              <div className="space-y-2">
                <Label>Store (optional)</Label>
                <Select value={form.store_id} onValueChange={v => setForm({ ...form, store_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select store" /></SelectTrigger>
                  <SelectContent>
                    {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-500 space-y-1.5">
              <div>Tickets are automatically assigned to the department head based on category.</div>
              {(triaging || triage) && (
                <div className="flex flex-wrap items-center gap-1.5 text-xs" data-testid="ticket-triage-suggestion">
                  <Sparkles className={`w-3.5 h-3.5 text-orange-500 ${triaging ? 'animate-pulse' : ''}`} />
                  {triaging && !triage ? <span className="text-slate-400">Rooty is reading your ticket…</span> : (
                    <>
                      <span className="font-medium text-slate-700">Rooty suggests:</span>
                      <CategoryBadge category={triage.category.value} />
                      <PriorityBadge priority={triage.priority.value} />
                      {triage.needs_owner_now >= 0.8 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium" data-testid="ticket-owner-alert">
                          <AlertTriangle className="w-3 h-3" /> Owners will be alerted
                        </span>
                      )}
                      {(touched.category || touched.priority) && (
                        <button type="button" className="underline text-orange-600" onClick={() => { setTouched({ category: false, priority: false }); setForm(f => ({ ...f, category: triage.category.value, priority: triage.priority.value })); }}>
                          Apply
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold" data-testid="submit-ticket-btn">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</> : <><Send className="w-4 h-4 mr-2" /> Submit Ticket</>}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <TicketDetailDialog
        ticket={selectedTicket}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onUpdate={refreshDetail}
        user={user}
      />
    </div>
  );
}
